/**
 * Hook для рендеринга шаблонов
 * Кеширует результаты, использует debounce
 * Отменяет предыдущий рендер при новом запросе
 */

import { useRef, useCallback, useState } from 'react';
import { exportTemplateToBlob, type ExportOptions } from '../../features/template-export/lib/exportTemplate';

interface UseTemplateRendererReturn {
  /**
   * Рендерит шаблон и возвращает blob URL
   */
  render: (options: Omit<ExportOptions, 'fileName'>) => Promise<string>;
  
  /**
   * Флаг что сейчас идет рендеринг
   */
  isRendering: boolean;
  
  /**
   * Очищает кеш
   */
  clearCache: () => void;
}

/** Кеш держит и подобранный кегль: на попадании рендера нет, а сообщить надо. */
interface CachedRender {
  url: string;
  resolvedFontSize?: { fontSize: number; max: number };
}

export function useTemplateRenderer(): UseTemplateRendererReturn {
  const cacheRef = useRef<Map<string, CachedRender>>(new Map());
  const [isRendering, setIsRendering] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const render = useCallback(async (options: Omit<ExportOptions, 'fileName'>): Promise<string> => {
    // Генерируем ключ для кеша
    const cacheKey = JSON.stringify({
      backgroundColor: options.backgroundColor,
      useGradient: options.useGradient,
      backgroundColorEnd: options.backgroundColorEnd,
      backgroundAlpha: options.backgroundAlpha,
      backgroundAlphaEnd: options.backgroundAlphaEnd,
      textColor: options.textColor,
      useTwoColors: options.useTwoColors,
      textColorSecondary: options.textColorSecondary,
      fontFamily: options.fontFamily,
      fontUrl: options.fontUrl,
      fontName: options.fontName,
      fontSize: options.fontSize,
      logoUrl: options.logoUrl,
      logoPosition: options.logoPosition,
      logoSize: options.logoSize,
      textLineSpacing: options.textLineSpacing,
      imageUrl: options.imageUrl,
      text: options.text,
      textUppercase: options.textUppercase,
      textAlign: options.textAlign,
      fitMode: options.fitMode,
      fitCanvasToMedia: options.fitCanvasToMedia,
      showLine: options.showLine,
      lineColor: options.lineColor,
      lineHeight: options.lineHeight,
      linePosition: options.linePosition,
      fontVariationSettings: options.fontVariationSettings,
      textShadowEnabled: options.textShadowEnabled,
      textShadowColor: options.textShadowColor,
      textShadowBlur: options.textShadowBlur,
      textShadowWidth: options.textShadowWidth,
      textSoftShadowEnabled: options.textSoftShadowEnabled,
      textSoftShadowColor: options.textSoftShadowColor,
      textSoftShadowBlur: options.textSoftShadowBlur,
      textSoftShadowOffsetX: options.textSoftShadowOffsetX,
      textSoftShadowOffsetY: options.textSoftShadowOffsetY,
      textSoftShadowOpacity: options.textSoftShadowOpacity,
      textShadowOpacity: options.textShadowOpacity,
      postLayout: options.postLayout,
      accentColor: options.accentColor,
      cardColor: options.cardColor,
      cardInset: options.cardInset,
      bandHeight: options.bandHeight,
      frameUrl: options.frameUrl,
      frameTextX: options.frameTextX,
      frameTextY: options.frameTextY,
      frameTextWidth: options.frameTextWidth,
      frameTextVAlign: options.frameTextVAlign,
      autoFontMax: options.autoFontMax,
      bandGrow: options.bandGrow,
      // Ширина превью переводит ручной кегль из preview-space в render-space.
      // Без неё после смены ширины окна отдавался бы старый рендер, сделанный
      // в другом масштабе.
      sourceWidth: options.sourceWidth,
    });
    
    // ⚡ ОПТИМИЗАЦИЯ: Проверяем кеш ПЕРЕД отменой предыдущего рендера
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      console.log('✅ Template from cache');
      // Рендер не запускается — переигрываем сохранённый ответ, иначе форма
      // после первого же кеш-хита теряет авто-размер и потолок для A+.
      if (cached.resolvedFontSize) options.onResolvedFontSize?.(cached.resolvedFontSize);
      return cached.url;
    }
    
    // 🛑 ОТМЕНЯЕМ предыдущий рендер если он еще идет
    if (abortControllerRef.current) {
      console.log('🛑 Cancelling previous render');
      abortControllerRef.current.abort();
    }
    
    // Создаем новый AbortController для этого рендера
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setIsRendering(true);
      
      console.log('🎨 Rendering template...');
      
      // Рендерим в главном потоке
      let resolvedFontSize: CachedRender['resolvedFontSize'];
      const blob = await exportTemplateToBlob({
        ...options,
        onResolvedFontSize: (info) => {
          resolvedFontSize = info;
          options.onResolvedFontSize?.(info);
        },
      });
      
      // Проверяем не отменили ли нас
      if (abortController.signal.aborted) {
        console.log('⏭️ Render was cancelled');
        throw new Error('Render cancelled');
      }
      
      const url = URL.createObjectURL(blob);
      
      // Сохраняем в кеш
      cacheRef.current.set(cacheKey, { url, resolvedFontSize });
      
      console.log('✅ Template rendered');
      
      return url;
    } catch (error) {
      // Не логируем cancelled errors
      if (error instanceof Error && error.message !== 'Render cancelled') {
        console.error('❌ Render error:', error);
      }
      throw error;
    } finally {
      // Очищаем только если это был последний запущенный рендер
      if (abortControllerRef.current === abortController) {
        setIsRendering(false);
        abortControllerRef.current = null;
      }
    }
  }, []);
  
  const clearCache = useCallback(() => {
    cacheRef.current.forEach(entry => URL.revokeObjectURL(entry.url));
    cacheRef.current.clear();
    console.log('🗑️ Template cache cleared');
  }, []);
  
  return {
    render,
    isRendering,
    clearCache,
  };
}